import type { MenuItem } from '../sidemenu/sidemenu.component';

/**
 * Whether a single menu node is visible for the current user's enabled features and permissions.
 *
 * Gating semantics, in order:
 * - An absent or empty `features` list means no feature restriction (allowed regardless of flags).
 *   Otherwise at least one of `features` must be enabled.
 * - An absent (or empty-array) `viewPermission` means no permission restriction. A single key must
 *   be held; an array is any-of (the user must hold at least one), matching `hasPermissionGuard`.
 * - The two gates are ANDed: every present gate must be satisfied for the node to show.
 *
 * This is the single source of truth for per-node menu gating shared by the web headers and the
 * side menu. It replaced the former role-based gating during the permission-authorization
 * migration: nodes are gated by `viewPermission` (feature-visibility only), never by role. It
 * intentionally evaluates only the node passed in; use {@link filterVisibleMenuItems} to filter a
 * whole tree.
 *
 * @param item - The menu node to evaluate.
 * @param hasPermission - Predicate resolving whether the current user holds a permission key.
 * @param features - The current user's enabled feature flags.
 * @returns True when the node should be shown to the user.
 */
export function canShowMenuItem(item: MenuItem, hasPermission: (key: string) => boolean, features: string[]): boolean {
    return (
        (!item.features || item.features.length === 0 || features.some((f) => item.features?.some((feat) => feat === f))) &&
        hasRequiredViewPermission(item.viewPermission, hasPermission)
    );
}

/**
 * Resolves the permission gate for a menu node. Absent or empty means unrestricted; a single key
 * must be held; an array is any-of (held if at least one key matches), mirroring the route guard so
 * a menu entry can align with a route guarded on multiple keys.
 *
 * @param viewPermission - The node's permission gate: unset, a single key, or an any-of array.
 * @param hasPermission - Predicate resolving whether the current user holds a permission key.
 * @returns True when the permission gate imposes no restriction or is satisfied.
 */
function hasRequiredViewPermission(viewPermission: string | string[] | undefined, hasPermission: (key: string) => boolean): boolean {
    if (!viewPermission) {
        return true;
    }
    if (Array.isArray(viewPermission)) {
        return viewPermission.length === 0 || viewPermission.some((key) => hasPermission(key));
    }
    return hasPermission(viewPermission);
}

/**
 * Recursively filters a menu subtree to the entries the current user may see. Each node is gated by
 * {@link canShowMenuItem}, children are filtered recursively, and a container whose children are all
 * gated away is itself dropped so the migration to permission-gated children never leaves an empty
 * submenu rendered. A leaf (a node with no `children` array) is kept whenever it passes the gate.
 *
 * @param items - The menu nodes to filter.
 * @param hasPermission - Predicate resolving whether the current user holds a permission key.
 * @param features - The current user's enabled feature flags.
 * @returns A new tree containing only the visible nodes, preserving node order and shape.
 */
export function filterVisibleMenuItems(items: MenuItem[], hasPermission: (key: string) => boolean, features: string[]): MenuItem[] {
    return items
        .filter((item) => canShowMenuItem(item, hasPermission, features))
        .map((item) => (item.children ? { ...item, children: filterVisibleMenuItems(item.children, hasPermission, features) } : item))
        .filter((item) => item.children === undefined || item.children.length > 0);
}
